import { useEffect, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import CompanyRepository from '@/creditflowresearch/infrastructure/repository/CompanyRepository';
import useCorporateTopCompanyFilters
  from '@/creditflowresearch/application/TopCompanies/corporateTopCompanies/useCorporateTopCompanyFilters';
import {
  ICorporateTopFilterPayload, ICorporateTtmFilter, ITopPeriodCorporate,
} from '@/creditflowresearch/application/TopCompanies/corporateTopCompanies/interfaces';

interface IUsePeriodsCollectionReturn {
  periodsCollection: ITopPeriodCorporate[],
  selectedPeriod: ITopPeriodCorporate | null,
  setSelectedPeriod: (period: ITopPeriodCorporate) => void,
  isPeriodsFetching: boolean,
}

export const useCorporatePeriodsCollection = ({
  collection: serverPeriodsCollection = [],
  hasServerError,
}: {
  collection: ITopPeriodCorporate[],
  hasServerError: boolean,
}, serverFilters: ICorporateTopFilterPayload): IUsePeriodsCollectionReturn => {
  const container = getContainer();
  const { includedRole, dateFrom, dateTo, ...filters } = useCorporateTopCompanyFilters({ serverFilters });
  const [periodsCollection, setPeriodsCollection] = useState<ITopPeriodCorporate[]>(serverPeriodsCollection);
  const [selectedPeriod, setSelectedPeriod] = useState<ITopPeriodCorporate | null>(serverPeriodsCollection[0] || null);
  const [isPeriodsFetching, setIsPeriodsFetching] = useState<boolean>(true);

  const getPeriodsCollection = async (): Promise<void> => {
    const companyRepository = container.get<CompanyRepository>(CompanyRepository);
    setIsPeriodsFetching(false);
    try {
      const response = await companyRepository.getTopCompaniesCorporateTtmFilter({ filters });
      setPeriodsCollection(response.map((f: ICorporateTtmFilter) => ({
        value: f.name,
        label: f.name,
        dateFrom: f.dateFrom,
        dateTo: f.dateTo,
        disabled: !f.active,
      })));
    } finally {
      setIsPeriodsFetching(true);
    }
  };

  useEffect(() => {
    if (hasServerError) {
      getPeriodsCollection();
    }
  }, [hasServerError]);

  useEffect(() => {
    if (!hasServerError) {
      setPeriodsCollection(serverPeriodsCollection);
      setSelectedPeriod(serverPeriodsCollection[0] || null);
    }
  }, [serverPeriodsCollection]);

  return {
    periodsCollection,
    selectedPeriod,
    setSelectedPeriod,
    isPeriodsFetching,
  };
};
